import React, { useCallback, useContext, useEffect } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';

import {
  DateFilter,
  SelectAsyncFilter,
  Table,
  TableCell,
  TableRow,
  WithSpinner,
} from '../../../components';

import { config } from '../../../api/config';
import { formatDate, formatLocaleDate, isFilterActive } from '../../../utils';

import { ApplicationsContext } from './applications.context';
import { actions } from '../../../state';

import styles from '../monitoring.module.scss';

const PAGE_SIZE = config.DEFAULTS.recordsPerPage;

const ApplicationPage = ({
  dispatch,
  applications: { loading, applications },
}) => {
  const {
    filterBody,
    setFilterBody,
    phoneFilter,
    setPhoneFilter,
    dateTimeLtFilter,
    setDateTimeLtFilter,
    dateTimeGtFilter,
    setDateTimeGtFilter,
    sortOrder,
    setSortOrder,
    page,
    setPage,
  } = useContext(ApplicationsContext);

  const pageCount = Math.ceil(applications.length / PAGE_SIZE) || 1;

  const clearApplications = useCallback(() => {
    dispatch(actions.monitoring.clearApplicationsAction());
  }, [dispatch]);

  const fetchApplications = useCallback(
    data => {
      dispatch(actions.monitoring.getApplicationsAction(data));
    },
    [dispatch]
  );

  useEffect(() => () => clearApplications(), [clearApplications]);

  useEffect(() => {
    if (page > pageCount) {
      setPage(1);
    }
  }, [page, pageCount]);

  useEffect(() => {
    const offset = (page - 1) * PAGE_SIZE;
    fetchApplications({ ...(filterBody || {}), offset });
  }, [fetchApplications, filterBody, page]);

  const handleSubmit = name => {
    const generatedFilter = {};

    if (phoneFilter.length) {
      generatedFilter.user_phone_number = phoneFilter;
    }
    if (dateTimeGtFilter) {
      generatedFilter.date_from = formatDate(dateTimeGtFilter);
    }
    if (dateTimeLtFilter) {
      generatedFilter.date_to = formatDate(dateTimeLtFilter);
    }

    if (name) {
      setFilterBody({ ...generatedFilter });
    }
    setPage(1);
  };

  const tableHeader = [
    <SelectAsyncFilter
      title="Numeris"
      name="user_phone_number"
      valueType="string"
      params={{ model: 'clients', field: 'phone' }}
      filterState={phoneFilter}
      setFilterState={setPhoneFilter}
      onSubmit={handleSubmit}
      sortOrder={sortOrder}
      setSortOrder={setSortOrder}
      disableSorting={true}
      isActive={isFilterActive('select', phoneFilter)}
    />,
    <DateFilter
      title="Data ir laikas"
      name="created_at"
      lt={dateTimeLtFilter}
      setLt={setDateTimeLtFilter}
      gt={dateTimeGtFilter}
      setGt={setDateTimeGtFilter}
      disableSorting={true}
      onSubmit={handleSubmit}
      sortOrder={sortOrder}
      setSortOrder={setSortOrder}
      isActive={isFilterActive('date', dateTimeLtFilter, dateTimeGtFilter)}
      titleWidth={'19rem'}
    />,
    <div className={styles.tableHeader}>Pranešimo tipas</div>,
    <div className={styles.tableHeader}>Klaidos pranešimas</div>,
  ];

  const renderTable = () =>
    applications.map(
      ({ event_id, user_phone_number, created_at, type, title }) => (
        <TableRow key={event_id}>
          <TableCell>{user_phone_number || '-'}</TableCell>
          <TableCell>
            {created_at ? formatLocaleDate(created_at) : '-'}
          </TableCell>
          <TableCell>{type}</TableCell>
          <TableCell>{title}</TableCell>
        </TableRow>
      )
    );

  return (
    <WithSpinner loading={loading}>
      <Table
        header={tableHeader}
        isEmpty={!applications.length}
        filter={true}
        loading={loading}
        page={page}
        pageSize={PAGE_SIZE}
        total={applications.length}
        serverPagination={true}
        onPageChange={setPage}
      >
        {renderTable()}
      </Table>
    </WithSpinner>
  );
};

const mapStateToProps = state => ({
  applications: state.monitoring.applications,
});

ApplicationPage.propTypes = {
  dispatch: PropTypes.func.isRequired,
  applications: PropTypes.shape({
    loading: PropTypes.bool.isRequired,
    applications: PropTypes.array.isRequired,
  }).isRequired,
};

export default connect(mapStateToProps)(ApplicationPage);
